import type { Capabilities, MeResponse, Membership, WorkspaceRef } from "@ondo/shared";

import { About } from "../about/About.tsx";
import { ArtistSettlements } from "../collector/ArtistSettlements.tsx";
import { Contacts } from "../gallery/Contacts.tsx";
import { Activity } from "../activity/Activity.tsx";
import { Notifications } from "../notifications/Notifications.tsx";
import { NotificationPreferences } from "../notificationPreferences/NotificationPreferences.tsx";
import { NotBuiltYet } from "../shell/NotBuiltYet.tsx";
import { PortalShell } from "../shell/PortalShell.tsx";
import { Profile } from "../profile/Profile.tsx";
import { Verifications } from "../verifications/Verifications.tsx";
import { WorkRecord } from "../work/WorkRecord.tsx";
import { atLeast } from "@ondo/shared";
import { workspacePath } from "../routes/routes.model.ts";
import { inboxPrincipalFor } from "../shell/shell.model.ts";
import { AttributionFees } from "./AttributionFees.tsx";
import { Authentications } from "./Authentications.tsx";
import { Catalogue } from "./Catalogue.tsx";
import { LoansConsign } from "./LoansConsign.tsx";
import { Studio } from "./Studio.tsx";
import { ESTATE_PORTAL } from "./estate.constants.ts";
import type { EstateDestination } from "./estate.constants.ts";
import type { PortalView } from "../shell/shell.model.ts";

/**
 * The artist / estate workspace.
 *
 * The frame is `PortalShell`, shared with every other portal; what is here is
 * which component each destination renders — the one thing that genuinely
 * differs between portals.
 *
 * Half its screens are other portals', unchanged and unwrapped — the
 * verification inbox, the contacts read, the artist's settlement ledger —
 * which is the same argument the gallery and verifier make in the same words:
 * what an estate may see is decided in SQL, so an estate-shaped copy would be
 * a second opinion about that, written in TypeScript, in a place no policy can
 * correct. The screens that are this portal's own — the catalogue, the studio,
 * the loans desk — are the ones whose relations exist for the estate alone.
 */
export function EstateWorkspace({
  destination,
  record,
  workspace,
  principal,
  capabilities,
  membership,
  canSwitch,
  onSignOut,
}: {
  destination: EstateDestination;
  /** Which work a `work` destination is about. Null for every other one. */
  record: string | null;
  workspace: WorkspaceRef;
  principal: MeResponse["principal"];
  capabilities: Capabilities;
  /**
   * The caller's membership of this workspace's organisation, or null.
   *
   * Null is the SOLO case and it is real, not degenerate: an artist who
   * declared `artist_estate` at signup holds this workspace personally, with no
   * firm behind it — the shape Nadia holds `agent` in. Every rung check below
   * goes through `atLeast`, whose null branch answers yes for exactly that
   * reason: a personal workspace has no seats to be junior in.
   */
  membership: Membership | null;
  canSwitch: boolean;
  onSignOut: () => void;
}) {
  return (
    <PortalShell
      spec={ESTATE_PORTAL}
      destination={destination}
      workspace={workspace}
      principal={principal}
      capabilities={capabilities}
      membership={membership}
      canSwitch={canSwitch}
      onSignOut={onSignOut}
    >
      {(view) => (
        <Destination
          destination={destination}
          record={record}
          workspace={workspace}
          capabilities={capabilities}
          membership={membership}
          principal={principal}
          view={view}
          onSignOut={onSignOut}
        />
      )}
    </PortalShell>
  );
}

/** The one place a switch over the estate's destinations lives. */
function Destination({
  destination,
  record,
  workspace,
  capabilities,
  membership,
  principal,
  view,
  onSignOut,
}: {
  destination: EstateDestination;
  record: string | null;
  workspace: WorkspaceRef;
  capabilities: Capabilities;
  membership: Membership | null;
  principal: MeResponse["principal"];
  view: PortalView;
  onSignOut: () => void;
}) {
  switch (destination) {
    // The front page. `canDeclare` is the rung check `atLeast` exists for —
    // declining to offer a control the database is certain to refuse — and
    // null (a solo artist's personal workspace) answers yes, because there is
    // no seat to be junior in.
    case "catalogue":
      return (
        <Catalogue
          workBase={view.hrefs.workBase}
          canDeclare={atLeast(membership?.level, "admin")}
        />
      );

    // The SUBJECT's id — the organisation's, or the solo artist's own — is
    // what `ondo.register_studio_work()` names the studio by, and the admin
    // rung is what it requires: `atLeast` declines to offer a control the
    // database is certain to refuse, and answers yes for a personal workspace.
    case "studio":
      return (
        <Studio
          workBase={view.hrefs.workBase}
          studioOndoId={inboxPrincipalFor(principal, workspace)}
          canRegister={atLeast(membership?.level, "admin")}
        />
      );

    case "loans":
      return <LoansConsign />;

    // The gallery's screen, unchanged — nothing in `ondo.my_contact_edges()`
    // is gallery-specific: it is bounded by "the person AND every organisation
    // they are an active member of", so a studio seat reads the studio's
    // counterparties through the same read. Notes need editor, as there.
    // `actingAs` is null on a SOLO ARTIST'S workspace and that is a real
    // answer, not a gap: there is no organisation, the reader is acting as
    // themselves, and the stamp names no firm — see `ReaderStamp`.
    case "contacts":
      return (
        <Contacts
          workBase={view.hrefs.workBase}
          canNote={atLeast(membership?.level, "editor")}
          actingAs={workspace.organisation?.ondoId ?? null}
        />
      );

    // TWO LEDGERS, one centre — OND-70. The studio's authentication fees
    // (`ondo.attribution_fees`, read-only here; the acts on a fee stay on the
    // Verifications tab beside the case) above the artist's settlement ledger —
    // royalties owed when a covenanted work resells — which shipped on the
    // collector's rail because the estate had none. Mounted, not copied: what
    // an estate is owed is decided by `ondo.artist_settlements()` and both
    // rails must show the same rows, and the per-currency totals strip it now
    // carries is the same object on both.
    case "transactions":
      return (
        <>
          <AttributionFees />
          <ArtistSettlements workBase={view.hrefs.workBase} />
        </>
      );

    // Scoped to the ESTATE on an organisation's workspace — see
    // `inboxPrincipalFor`, which is where the argument is. Unlike the gallery,
    // this workspace CAN be personal (a solo artist), and then the fallback to
    // the reader's own id is doing real work rather than staying total.
    case "verifications":
      return <Verifications principalOndoId={inboxPrincipalFor(principal, workspace)} />;

    // The estate's own queue — OND-212. Scoped to the estate exactly as the
    // inbox above is, because it IS that inbox narrowed to the authorship
    // claim, with the service and the confirmed works beside it.
    case "authentications":
      return (
        <Authentications
          principalOndoId={inboxPrincipalFor(principal, workspace)}
          workBase={view.hrefs.workBase}
          catalogueHref={workspacePath(workspace, "catalogue")}
        />
      );

    // `workspace` AND an id, which look redundant and are not: the workspace
    // decides where each row's action LINKS to, and the id decides whose feed
    // is read. On a firm workspace those are two different principals.
    case "notifications":
      return (
        <Notifications
          workspace={workspace}
          inboxOndoId={inboxPrincipalFor(principal, workspace)}
        />
      );

    // The FIRM's preferences on a firm workspace, exactly as the inbox above is
    // the firm's inbox — and writable only by somebody who administers it,
    // because choosing how an organisation is told things is an act of the
    // organisation. `ondo.set_notification_preference()` refuses it either way;
    // this is what stops the screen offering a control that would be refused.
    case "notification-preferences":
      return (
        <NotificationPreferences
          principalOndoId={inboxPrincipalFor(principal, workspace)}
          canWrite={atLeast(membership?.level, "admin")}
          isFirm={workspace.organisation !== null}
        />
      );

    case "about":
      return <About />;

    // The PERSON's own ONDO id, even on the studio's workspace, so their acts
    // read "You" rather than their name. The opposite of the inbox above and
    // deliberately so: an inbox is addressed to a principal, a trail is read by
    // a reader, and the reader is always the person signing in.
    case "activity":
      // `administers` CHANGES NO ROWS. The trail's contents are decided by
      // `access_events_read` — a viewer's own reads, an administrator's whole
      // firm — and this only picks which sentence an EMPTY trail gets, so an
      // ordinary seat is never told the firm has opened nothing when the truth
      // is that they have. `atLeast` is the same derivation the rest of this
      // file uses for a rung; no new capability was invented for it.
      return (
        <Activity
          workBase={view.hrefs.workBase}
          viewerOndoId={principal.ondoId}
          administers={atLeast(membership?.level, "admin")}
        />
      );

    // Takes the workspace rather than the principal, because WHOSE profile this
    // is follows from whether the workspace has an organisation.
    case "profile":
      return <Profile workspace={workspace} onSignOut={onSignOut} />;

    // `record` is non-null here by construction: `resolveWorkspaceRoute` sends a
    // work destination without one to `recordFallback`.
    case "work":
      return record ? (
        <WorkRecord
          workId={record}
          // The catalogue is where an estate chooses a work from — it holds
          // no collection by virtue of being one.
          collectionHref={view.hrefs.recordFallback}
          // The server's answer, not an assumption from the rail: an estate that
          // OWNS a work of its own may assign an agent to it through the same
          // account, and the panel hides itself when the answer is no.
          canAssignAgent={capabilities.assignAgent}
          canWriteHistory={atLeast(membership?.level, "editor")}
          // OND-68, and the same value Contacts passes. An estate workspace can
          // be an individual artist's as well as a studio's, so null here is a
          // real answer and not a gap: a reader with no firm has no firm's
          // trail to appear in.
          actingAs={workspace.organisation?.ondoId ?? null}
        />
      ) : null;

    // The catalogue, the studio, contacts, loans, the Transaction Centre and
    // the authentication queue. Each renders its `notYet` copy from the portal
    // spec, which names the RELATION it waits on rather than saying "coming
    // soon" — and each block must be rewritten the day its relation lands,
    // because a placeholder naming a table that now exists is worse than none.
    default:
      return <NotBuiltYet copy={view.notYet} />;
  }
}
